// MACHINERY V2 (Stroje) — catalog page chrome: breadcrumb, title band, model
// search box (M2.8).
//
// Spec: data package §11 (`query.search` row). Artboards 0900 / 0901 / 0902
// draw the band: "STROJE · KATALÓG" over "Katalóg strojov", with the model
// search box right-aligned on the browse page.
//
// THE BREADCRUMB IS THE BACK AFFORDANCE. Artboard 0902 draws a separate
// "‹ Späť do katalógu" link that OVERLAPS the breadcrumb line — a collision in
// the board, not a spec for two controls. The KATALÓG hop already goes exactly
// where that link goes, so the detail page renders one breadcrumb whose last
// clickable hop IS "back to catalog", and the overlap disappears. Recorded as a
// deliberate divergence.
//
// It uses the mono/uppercase eyebrow the artboards draw for this surface rather
// than the 12.5px sans hops of sr/SrPageBreadcrumb.tsx, but keeps that file's
// band geometry (10px/24px, border-soft underneath) so the two pages still read
// as one application.
//
// Every hop targets the V2 surface. `/machinery` is never linked from here —
// M2.8 also repointed the ONE v1 catalog link that remained
// (add/AddMachinePicker.tsx `CATALOG_PATH`).
//
// No Tailwind, no shadcn, no MUI, no @emotion, no sx, no dt tokens.
import React from "react";
import { useNavigate } from "react-router-dom";

import useTranslation from "../../../../shared/hooks/useTranslation";
import { MACHINERY_V2_BASE } from "../sr/srNavigation";

export const CATALOG_V2_PATH = `${MACHINERY_V2_BASE}/catalog`;

/**
 * Where a successful "add from catalog" lands — used by BOTH catalog surfaces.
 *
 * The catalog pages have no post-add state of their own: `createMachine`
 * fulfilled sets no success flag for MachineryToastHost to render, nothing
 * refetches, and the model's `unitsOnPlatform` still shows the pre-add count.
 * Closing the dialog and staying put is therefore indistinguishable from
 * Cancel, and the natural response is to submit again and create a duplicate.
 * v1 solves it by leaving for the fleet with the new id in the query string
 * (components/CatalogBrowsePage.tsx:1204 and CatalogModelDetailPage.tsx:652,
 * both `navigate('/machinery?newMachine={id}')`), where the NEW badge is the
 * confirmation — data package §11 Actions records that as the contract. v2
 * keeps it and only repoints the destination at the v2 fleet, which already
 * consumes the param (fleet/FleetPage.tsx:92-99).
 */
export const catalogAddedPath = (id?: string): string =>
  id ? `${MACHINERY_V2_BASE}?newMachine=${id}` : MACHINERY_V2_BASE;

const hopStyle: React.CSSProperties = {
  padding: 0,
  border: 0,
  background: "transparent",
  cursor: "pointer",
  fontFamily: "var(--font-mono)",
  fontSize: 9.5,
  letterSpacing: "0.09em",
  textTransform: "uppercase",
  color: "var(--mod-accent)",
  minWidth: 0,
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
};

const Sep: React.FC = () => (
  <span style={{ color: "var(--faint)", flexShrink: 0 }} aria-hidden>
    ·
  </span>
);

export interface CatalogBreadcrumbProps {
  /** Present on the detail page — the non-clickable tail. */
  current?: string;
}

export const CatalogBreadcrumb: React.FC<CatalogBreadcrumbProps> = ({ current }) => {
  const { translate } = useTranslation("myMachinery");
  const navigate = useNavigate();

  return (
    <nav
      aria-label={translate("myMachinery:machineCatalog")}
      style={{
        padding: "10px 24px",
        borderBottom: "1px solid var(--border-soft)",
        display: "flex",
        alignItems: "center",
        gap: 8,
        minWidth: 0,
        flexWrap: "wrap",
      }}
    >
      <button type="button" style={hopStyle} onClick={() => navigate(MACHINERY_V2_BASE)}>
        {translate("myMachinery:breadcrumbEquipment")}
      </button>
      <Sep />
      {current ? (
        <React.Fragment>
          <button type="button" style={hopStyle} onClick={() => navigate(CATALOG_V2_PATH)}>
            {translate("myMachinery:machineCatalog")}
          </button>
          <Sep />
          <span
            title={current}
            style={{
              fontFamily: "var(--font-mono)",
              fontSize: 9.5,
              letterSpacing: "0.09em",
              textTransform: "uppercase",
              color: "var(--ink-2)",
              minWidth: 0,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {current}
          </span>
        </React.Fragment>
      ) : (
        <span
          style={{
            fontFamily: "var(--font-mono)",
            fontSize: 9.5,
            letterSpacing: "0.09em",
            textTransform: "uppercase",
            color: "var(--ink-2)",
            minWidth: 0,
          }}
        >
          {translate("myMachinery:machineCatalog")}
        </span>
      )}
    </nav>
  );
};

export const CatalogTitle: React.FC<{ children?: React.ReactNode }> = ({ children }) => {
  const { translate } = useTranslation("myMachinery");
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 14,
        padding: "14px 24px 0",
        flexWrap: "wrap",
        minWidth: 0,
      }}
    >
      <h1
        style={{
          margin: 0,
          fontSize: 24,
          fontWeight: 700,
          letterSpacing: "-0.02em",
          color: "var(--ink)",
          minWidth: 0,
        }}
      >
        {translate("myMachinery:machineCatalog")}
      </h1>
      <span style={{ flex: 1 }} />
      {children}
    </div>
  );
};

/** The MODEL search — server-side, debounced, distinct from the rail's
 *  client-side brand box (catalogFacets.ts explains the split). */
export const CatalogSearchBox: React.FC<{
  value: string;
  onChange: (value: string) => void;
  onSubmit: () => void;
  onClear: () => void;
}> = ({ value, onChange, onSubmit, onClear }) => {
  const { translate } = useTranslation("myMachinery");

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 7,
        height: 36,
        width: 320,
        maxWidth: "100%",
        padding: "0 11px",
        borderRadius: 9,
        border: "1px solid var(--border)",
        background: "var(--card)",
        minWidth: 0,
      }}
    >
      <svg
        viewBox="0 0 12 12"
        width="12"
        height="12"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.4"
        style={{ color: "var(--faint)", flexShrink: 0 }}
        aria-hidden
      >
        <circle cx="5" cy="5" r="3.4" />
        <path d="M7.6 7.6 L10.5 10.5" strokeLinecap="round" />
      </svg>
      <input
        type="text"
        value={value}
        aria-label={translate("myMachinery:searchCatalogModels")}
        placeholder={translate("myMachinery:searchCatalogModels")}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter") onSubmit();
        }}
        style={{
          flex: 1,
          minWidth: 0,
          border: 0,
          outline: "none",
          background: "transparent",
          color: "var(--ink)",
          fontFamily: "inherit",
          fontSize: 13,
        }}
      />
      {value && (
        <button
          type="button"
          className="btn btn-ghost"
          onClick={onClear}
          aria-label={translate("myMachinery:v2.tabs.clearSearch")}
          style={{ width: 22, height: 22, padding: 0, justifyContent: "center", fontSize: 13 }}
        >
          &times;
        </button>
      )}
    </div>
  );
};
